import React from 'react';

import {
	aiConfigItemMessages,
	aiMessages,
	aiLanguageMessages,
} from '@atlaskit/editor-common/ai-messages';
import {
	AI_ASK_ROVO_MENU_ITEM,
	AI_IMPROVE_WRITING_MENU_ITEM,
	AI_POLISH_MENU_ITEM,
	AI_FIX_SPELLING_AND_GRAMMAR_MENU_ITEM,
	AI_MAKE_SHORTER_MENU_ITEM,
	AI_MAKE_LONGER_MENU_ITEM,
	AI_CHANGE_TONE_MENU_ITEM,
	AI_CHANGE_TONE_PROFESSIONAL_MENU_ITEM,
	AI_CHANGE_TONE_CASUAL_MENU_ITEM,
	AI_CHANGE_TONE_EMPATHETIC_MENU_ITEM,
	AI_TRANSLATE_MENU_ITEM,
} from '@atlaskit/editor-common/block-menu';
import {
	AICommandIcon,
	AISpellcheckIcon,
	AIShortenIcon,
	AILengthenIcon,
	AIBriefcaseIcon,
	AICasualIcon,
	AIHeartIcon,
	AITranslateIcon,
} from '@atlaskit/editor-toolbar';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import ChangeToneIcon from '@atlaskit/icon/core/microphone';
import { RovoIcon } from '@atlaskit/logo';

import type { EditorPluginAIProvider } from '../../types/types';
import {
	changeToneToCasual,
	changeToneToEmpathetic,
	changeToneToProfessional,
} from '../prebuilt/config-items/prompts/change-tone/change-tone';
import {
	improveWriting,
	fixSpellingAndGrammar,
} from '../prebuilt/config-items/prompts/enhance/enhance';
import { makeLonger } from '../prebuilt/config-items/prompts/make-longer/make-longer';
import { makeShorter } from '../prebuilt/config-items/prompts/make-shorter/make-shorter';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';
import { createTranslationConfigItems } from '../prebuilt/config-items/prompts/translate/translate';
import { getSortedSupportedTranslationLanguages } from '../prebuilt/config-items/utils/translation-utils';

import type { BlockMenuActionDef } from './types';

/**
 * Default character threshold for determining whether a block is "large".
 * Blocks with text content length >= this value are considered large.
 */
const LARGE_TEXT_THRESHOLD = 200;

/**
 * Dynamically generates translate children from the supported translation
 * languages list — mirroring the pattern in toolbar.tsx.
 */
const getTranslateChildren = (): BlockMenuActionDef[] => {
	const translationConfigItemsMap = createTranslationConfigItems();

	return getSortedSupportedTranslationLanguages().map(
		(lang): BlockMenuActionDef => ({
			key: `block-menu-item-ai-translate-${lang}`,
			label: aiLanguageMessages[lang],
			elemBefore: <AITranslateIcon label="" size="small" />,
			configItem: translationConfigItemsMap[lang],
			shouldShowInPrimary: () => false,
		}),
	);
};

export const getBlockMenuActions = ({
	editorPluginAIProvider,
}: {
	editorPluginAIProvider: EditorPluginAIProvider;
}): BlockMenuActionDef[] => {
	return [
		{
			key: AI_ASK_ROVO_MENU_ITEM.key,
			label: aiMessages.askRovoToolbarIconTitle,
			elemBefore: <RovoIcon label="" size="xxsmall" />,
			configItem: editorPluginAIProvider.baseGenerate,
			shouldShowInPrimary: () => true,
		},
		{
			key: AI_IMPROVE_WRITING_MENU_ITEM.key,
			label: aiConfigItemMessages.improveWritingTitle,
			elemBefore: <AICommandIcon label="" size="small" />,
			configItem: improveWriting(),
			shouldShowInPrimary: ({ textLength }) => textLength < LARGE_TEXT_THRESHOLD,
		},
		{
			key: AI_POLISH_MENU_ITEM.key,
			label: aiConfigItemMessages.addPolishTitle,
			elemBefore: <AiGenerativeCleanupIcon label="" size="small" />,
			configItem: addPolish(),
			shouldShowInPrimary: ({ textLength }) => textLength >= LARGE_TEXT_THRESHOLD,
		},
		// Always-nested actions — shouldShowInPrimary: () => false
		{
			key: AI_FIX_SPELLING_AND_GRAMMAR_MENU_ITEM.key,
			label: aiConfigItemMessages.fixSpellingGrammarTitle,
			elemBefore: <AISpellcheckIcon label="" size="small" />,
			configItem: fixSpellingAndGrammar(),
			shouldShowInPrimary: () => false,
		},
		{
			key: AI_CHANGE_TONE_MENU_ITEM.key,
			label: aiConfigItemMessages.changeToneNestingParentTitlePostGA,
			elemBefore: <ChangeToneIcon label="" size="small" />,
			shouldShowInPrimary: () => false,
			children: [
				{
					key: AI_CHANGE_TONE_PROFESSIONAL_MENU_ITEM.key,
					label: aiConfigItemMessages.changeToneProfessionalToneShortTitle,
					configItem: changeToneToProfessional(),
					elemBefore: <AIBriefcaseIcon label="" size="small" />,
					shouldShowInPrimary: () => false,
				},
				{
					key: AI_CHANGE_TONE_CASUAL_MENU_ITEM.key,
					label: aiConfigItemMessages.changeToneCasualToneShortTitle,
					configItem: changeToneToCasual(),
					elemBefore: <AICasualIcon label="" size="small" />,
					shouldShowInPrimary: () => false,
				},
				{
					key: AI_CHANGE_TONE_EMPATHETIC_MENU_ITEM.key,
					label: aiConfigItemMessages.changeToneEmpatheticToneShortTitle,
					configItem: changeToneToEmpathetic(),
					elemBefore: <AIHeartIcon label="" size="small" />,
					shouldShowInPrimary: () => false,
				},
			],
		},
		{
			key: AI_MAKE_SHORTER_MENU_ITEM.key,
			label: aiConfigItemMessages.makeShorterTitle,
			elemBefore: <AIShortenIcon label="" size="small" />,
			configItem: makeShorter(),
			shouldShowInPrimary: () => false,
		},
		{
			key: AI_MAKE_LONGER_MENU_ITEM.key,
			label: aiConfigItemMessages.makeLongerTitle,
			elemBefore: <AILengthenIcon label="" size="small" />,
			configItem: makeLonger(),
			shouldShowInPrimary: () => false,
		},
		{
			key: AI_TRANSLATE_MENU_ITEM.key,
			label: aiMessages.translateOptionsIconLabel,
			elemBefore: <AITranslateIcon label="" size="small" />,
			shouldShowInPrimary: () => false,
			children: getTranslateChildren(),
		},
	];
};
